import { api, apiErrorMessage, useAuthStore } from '@bemor/web-kit';
import {
    Button,
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    DateTime,
    Field,
    Input,
    SearchableSelect,
} from '@bemor/web-kit/ui';
import { useState } from 'react';

import type { TaxCode, TaxCodeSync } from '@/api/types';
import { optionsOf } from '@/lib/options';
import { P } from '@/lib/permissions';
import { useOne, useWrite } from '@/lib/queries';

const CATEGORY_TYPES = ['standard', 'custom'] as const;

/**
 * BM-63 decision 3, BM-67: the tenant's tax-code list with the tax provider category platform staff map each code
 * to, and the sync that creates the codes as products at the provider. A code never synced cannot sell once the
 * tenant's tax provider integration is in place. Read under payments.tax.read, written under catalogue.tax_codes.manage.
 */
export function TaxCodesSection() {
    const can = useAuthStore((s) => s.can);
    const codes = useOne<TaxCode[]>('/staff/tax-codes?per_page=100');
    const [error, setError] = useState<string | null>(null);
    const [draft, setDraft] = useState({
        code: '',
        label: '',
        provider_category_type: 'standard',
        provider_category_id: '',
    });
    const put = useWrite(
        async (body: {
            code: string;
            label: string;
            provider_category_type: string | null;
            provider_category_id: string | null;
        }) =>
            (
                await api().put<TaxCode>(`/staff/tax-codes/${body.code}`, {
                    label: body.label,
                    provider_category_type: body.provider_category_type,
                    provider_category_id: body.provider_category_id,
                })
            ).data,
        ['/staff/tax-codes'],
    );
    const sync = useWrite(
        async () =>
            (await api().post<TaxCodeSync>('/staff/tax-codes/sync-provider'))
                .data,
        ['/staff/tax-codes'],
    );
    const onError = (err: unknown) => setError(apiErrorMessage(err));
    const rows = codes.data?.data ?? [];
    // the PUT and the sync need catalogue.tax_codes.manage: the form shows only to someone the service lets in
    const manage = can(P.taxCodesManage);
    return (
        <Card data-testid="tenant-tax-codes">
            <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
                <CardTitle className="text-sm font-medium">
                    Tax codes and provider categories
                </CardTitle>
                {can(P.taxCodesManage) ? (
                    <Button
                        size="sm"
                        variant="outline"
                        disabled={sync.isPending}
                        onClick={() => {
                            setError(null);
                            sync.mutate(undefined, { onError });
                        }}
                    >
                        Sync with the tax provider
                    </Button>
                ) : null}
            </CardHeader>
            <CardContent className="space-y-4">
                <p className="text-muted-foreground text-xs">
                    Each code is a product at the tax provider; the sync creates
                    it under the mapped product tax category. A code never
                    synced cannot sell once a tax provider is connected.
                </p>
                {error ? (
                    <p role="alert" className="text-destructive text-sm">
                        {error}
                    </p>
                ) : null}
                {sync.data ? (
                    <p className="text-sm" data-testid="tax-codes-synced">
                        Synced {sync.data.synced} code
                        {sync.data.synced === 1 ? '' : 's'} with the provider.
                    </p>
                ) : null}
                <ul className="divide-y text-sm" data-testid="tax-codes">
                    {rows.map((c) => (
                        <li
                            key={c.code}
                            className="flex flex-wrap items-center justify-between gap-2 py-2"
                        >
                            <span>
                                <span className="font-mono text-xs">
                                    {c.code}
                                </span>
                                <span className="ml-2">{c.label}</span>
                                <span className="text-muted-foreground ml-2 text-xs">
                                    {c.provider_category_id
                                        ? `${c.provider_category_type ?? ''} ${c.provider_category_id}`
                                        : 'no provider category'}
                                </span>
                            </span>
                            <span className="text-xs">
                                {c.provider_synced_at ? (
                                    <>
                                        synced{' '}
                                        <DateTime
                                            value={c.provider_synced_at}
                                            empty="—"
                                        />
                                    </>
                                ) : (
                                    'not synced'
                                )}
                            </span>
                        </li>
                    ))}
                    {rows.length === 0 && !codes.isLoading ? (
                        <li className="text-muted-foreground py-2">
                            No tax codes yet.
                        </li>
                    ) : null}
                </ul>
                {manage ? (
                    <form
                        method="post"
                        className="space-y-3"
                        data-testid="tax-code-form"
                        onSubmit={(e) => {
                            e.preventDefault();
                            setError(null);
                            const hasCategory =
                                draft.provider_category_id.trim() !== '';
                            put.mutate(
                                {
                                    code: draft.code.trim(),
                                    label: draft.label.trim(),
                                    provider_category_type: hasCategory
                                        ? draft.provider_category_type
                                        : null,
                                    provider_category_id: hasCategory
                                        ? draft.provider_category_id.trim()
                                        : null,
                                },
                                {
                                    onError,
                                    onSuccess: () =>
                                        setDraft({
                                            code: '',
                                            label: '',
                                            provider_category_type: 'standard',
                                            provider_category_id: '',
                                        }),
                                },
                            );
                        }}
                    >
                        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
                            <Field id="tax_code" label="Code">
                                <Input
                                    id="tax_code"
                                    className="font-mono text-xs"
                                    placeholder="SW054000"
                                    value={draft.code}
                                    onChange={(e) =>
                                        setDraft({
                                            ...draft,
                                            code: e.target.value,
                                        })
                                    }
                                />
                            </Field>
                            <Field id="tax_label" label="Label">
                                <Input
                                    id="tax_label"
                                    value={draft.label}
                                    onChange={(e) =>
                                        setDraft({
                                            ...draft,
                                            label: e.target.value,
                                        })
                                    }
                                />
                            </Field>
                            <Field
                                id="provider_category_type"
                                label="Provider category type"
                            >
                                <SearchableSelect
                                    id="provider_category_type"
                                    label="Provider category type"
                                    options={optionsOf(CATEGORY_TYPES)}
                                    value={draft.provider_category_type}
                                    clearable={false}
                                    onChange={(v) =>
                                        v
                                            ? setDraft({
                                                  ...draft,
                                                  provider_category_type: v,
                                              })
                                            : undefined
                                    }
                                />
                            </Field>
                            <Field
                                id="provider_category_id"
                                label="Provider category id"
                                hint="From the provider's product tax categories (Anrok: productTaxCategories); empty leaves the code unmapped."
                            >
                                <Input
                                    id="provider_category_id"
                                    className="font-mono text-xs"
                                    value={draft.provider_category_id}
                                    onChange={(e) =>
                                        setDraft({
                                            ...draft,
                                            provider_category_id:
                                                e.target.value,
                                        })
                                    }
                                />
                            </Field>
                        </div>
                        <Button
                            type="submit"
                            variant="outline"
                            disabled={
                                put.isPending ||
                                draft.code.trim() === '' ||
                                draft.label.trim() === ''
                            }
                        >
                            Save tax code
                        </Button>
                    </form>
                ) : null}
            </CardContent>
        </Card>
    );
}
